import { COMPANY, displayIdType, formatCompanyLocality, type VoucherWithInvoiceDto } from '@gty/common';

import {
  CARRIER_OR_NUMBER_FALLBACK_LABEL,
  TRANSPORT_LEG_CARRIER_LABEL,
  TRANSPORT_LEG_MODE_LABEL,
} from '../documents/VoucherTransportLegFields';
import { VOUCHER_HOTEL_DETAILS_NOTE } from '../documents/VoucherHotelDetailFields';
import { DEFAULT_CANCELLATION_POLICY_TEXT } from '../documents/VoucherEditor';
import { cn } from '../ui/cn';
import { formatCurrency, formatShortDate } from '../ui/format';
import {
  printedGender,
  resolveVoucherContent,
  UNKNOWN,
  type VoucherBookingContext,
  type VoucherPrintModel,
} from './voucher-content';

/**
 * The two-page A4 booking confirmation voucher.
 *
 * Rebuilt directly from the `isVouchers` screen in `Guruji ERP.dc.html` — the
 * type scale, palette, section order and column splits below are transcribed
 * from that file rather than approximated:
 *
 *   page 1  header · trust strip · Guest Information · Tour Details ·
 *           Passenger Details
 *   page 2  Transport Details (when the voucher has transport legs) ·
 *           Hotel Details (when the voucher has hotel rows, item 5) ·
 *           Package Includes/Excludes · Important Notes ·
 *           Cancellation Policy · Payment Summary · footer
 *
 * Page 1 used to also carry its OWN "Hotel Details" card, reading
 * `invoice.hotels` — empty for any voucher at token stage, since no invoice
 * exists yet. Retired for the same reason the old vendor-based
 * "Stay / Services" card was: one voucher-owned hotel section (item 5,
 * page 2), not two overlapping, inconsistently-populated ones. The
 * `InvoiceHotel` table and its rows are untouched; only this page stopped
 * rendering them.
 *
 * Cancellation Policy (item 2) used to print a Window/Charge table read from
 * `content.cancellation` (`Invoice.cancellation`/`InvoiceCancellationRow`) —
 * a real, structured DB table, but one with NO editing UI anywhere in the
 * product, so it never carried real data. Replaced with the voucher's OWN
 * free-text snapshot of the tour's policy (`voucher.cancellationPolicy`),
 * falling back to `DEFAULT_CANCELLATION_POLICY_TEXT` when neither was ever
 * set. `InvoiceCancellationRow` and `content.cancellation` are untouched;
 * only this page stopped reading them.
 *
 * FIXED 794 x 1123px per sheet. At 96dpi that is exactly A4, so what renders on
 * screen is what prints, and the forthcoming Puppeteer pass can photograph this
 * component without the layout shifting. Nothing here is responsive on purpose.
 *
 * OVERFLOW IS THE OTHER HALF OF THE JOB. Every table cell that takes free text
 * wraps rather than truncating, and the sheets are `overflow-hidden` so a long
 * address can never spill outside the page edge — the specific complaint this
 * rebuild is answering.
 */

/* ── design tokens, transcribed from the export ───────────────────────────── */

const INK = '#1a1a1a';
const RULE = '#e0d7d5';
const MUTED = '#8a8380';

/** Red section tab that sits on a bordered card's top edge. */
function Tab({ label }: { label: string }): JSX.Element {
  return (
    <div className="absolute -top-[9px] left-3 z-10 inline-flex items-center rounded-[4px] bg-brand px-[11px] py-[5px]">
      <span className="text-[8.6px] font-bold uppercase leading-[1.35] tracking-[.5px] text-white">
        {label}
      </span>
    </div>
  );
}

function Card({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section
      className={cn('relative rounded-[9px] border border-brand/30 px-3 pb-3 pt-[15px]', className)}
    >
      <Tab label={label} />
      {children}
    </section>
  );
}

/** "Label : value" row inside the Guest / Tour cards. */
function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-baseline gap-1.5">
      <span
        className="w-[104px] flex-none text-[10.4px] font-medium leading-[1.25]"
        style={{ color: MUTED }}
      >
        {label}
      </span>
      <span className="flex-none text-[10.4px] leading-[1.25]" style={{ color: MUTED }}>
        :
      </span>
      {/* `min-w-0` + `break-words` is what keeps a long address inside the card. */}
      <span className="min-w-0 flex-1 break-words text-[10.4px] font-semibold leading-[1.25]">
        {value || '—'}
      </span>
    </div>
  );
}

const TH =
  'px-2 py-[7px] text-left text-[8.6px] font-semibold uppercase leading-[1.35] tracking-[.5px] text-white';
const TD = 'px-2 py-[7px] align-top text-[10.1px] leading-[1.3] break-words';

function Sheet({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <article
      data-print-sheet
      className="relative h-[1123px] w-[794px] flex-none overflow-hidden bg-white shadow-[0_10px_34px_rgba(0,0,0,.14)]"
      style={{ color: INK }}
    >
      {/* Left brand rule and the corner wedge, both from the export. */}
      <div className="absolute bottom-0 left-0 top-0 w-[7px] bg-gradient-to-b from-brand to-brand-dark" />
      <div className="absolute -right-[58px] -top-[56px] h-[92px] w-[132px] rounded-bl-full bg-gradient-to-tl from-brand-dark to-brand" />
      <div className="flex h-full flex-col">{children}</div>
    </article>
  );
}

/** A date that may not be known yet — an invoice-only field at token stage. */
function maybeDate(iso: string | null): string {
  return iso === null ? UNKNOWN : formatShortDate(iso);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * A `WallClockDateTime` (`2026-07-30T01:20`) -> "30 Jul 2026, 1:20 AM".
 *
 * TEXT IN, TEXT OUT. The value is the time printed on the ticket with no zone,
 * so it is reformatted from its own digits — never handed to `new Date` or a
 * locale formatter, either of which would apply the viewer's offset and print a
 * different time from the ticket. Anything not in the expected shape prints
 * verbatim rather than as a guess. 12-hour with an explicit AM/PM, not 24-hour
 * — the same product decision `WallClockPicker` makes on the input side.
 */
function formatWallClock(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (match === null) return value;
  const [, year, month, day, hourStr, minute] = match;
  const monthName = MONTHS[Number(month) - 1];
  if (monthName === undefined) return value;
  const hour24 = Number(hourStr);
  const period = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${day} ${monthName} ${year}, ${hour12}:${minute} ${period}`;
}

/** The full letterhead — page 1 only. */
function Letterhead({
  v,
  content,
}: {
  v: VoucherWithInvoiceDto;
  content: VoucherPrintModel;
}): JSX.Element {
  const confirmed = v.voucher.bookingStatus === 'CONFIRMED';
  return (
    <header className="relative px-7 pt-[18px]">
      {/*
        `items-center` centres the brand block vertically against the taller
        meta column, which puts it midway between the page edge and the red rule
        below — it used to sit hard against the top on `items-start`.
      */}
      <div className="flex items-center gap-3">
        <img src="/brand/guruji-mark.png" alt="" className="h-auto w-[58px] flex-none" />
        <div className="pt-[2px]">
          <div className="font-heading text-[29px] leading-[.92] tracking-[-.8px] text-brand">
            GURUJI
          </div>
          <div className="mt-[6px] text-[16px] font-bold leading-none tracking-[1.1px]">
            TRAVEL YATRA
          </div>
          <div className="mt-[6px] h-[2px] w-[152px] bg-brand" />
          <div className="mt-[5px] text-[12px] leading-none" style={{ color: '#2a2726' }}>
            {COMPANY.tagline}{' '}
            <span className="font-semibold text-brand">{COMPANY.taglineAccent}</span>
          </div>
        </div>

        {/*
          `mt-[26px]` clears the red corner wedge.
          The wedge is 132x92 offset -58/-56, so its visible area is roughly
          x > 720px and y < 36px. The header's own 18px top padding plus this
          margin starts the title at ~44px — below the wedge, not behind it.
        */}
        <div className="ml-auto mt-[26px] w-[300px] flex-none text-right">
          {/*
            ONE line, not two. Split across two lines the first half sat inside
            the wedge and was unreadable; 15px keeps all 28 characters inside the
            300px column.
          */}
          <div className="text-[15px] font-heading leading-none tracking-[-.2px]">
            BOOKING CONFIRMATION <span className="text-brand">VOUCHER</span>
          </div>
          <span
            className="mt-[9px] inline-block rounded-[3px] px-[9px] py-[5px] text-[8px] font-bold uppercase leading-[1.2] tracking-[.6px] text-white"
            style={{ background: confirmed ? '#1c8a4d' : MUTED }}
          >
            {v.voucher.bookingStatus}
          </span>

          <table className="mt-[10px] w-full table-fixed border-collapse">
            <tbody>
              {[
                ['Voucher No.', v.voucher.ref, true],
                ['Booking Date', maybeDate(content.bookingDate), false],
                ['Issue Date', formatShortDate(v.voucher.issuedAt), false],
              ].map(([label, value, accent]) => (
                <tr key={String(label)}>
                  <td
                    className="w-[116px] py-[3px] text-left text-[10.2px] font-semibold leading-[1.25]"
                    style={{ color: MUTED }}
                  >
                    {label}
                  </td>
                  <td
                    className={cn(
                      'py-[3px] text-right text-[10.4px] font-heading leading-[1.25]',
                      accent === true && 'text-brand',
                    )}
                  >
                    {value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-3 h-[2px] bg-brand" />
    </header>
  );
}

const TRUST: ReadonlyArray<[string, string]> = [
  ['SAFE JOURNEY', 'OUR PRIORITY'],
  ['EXPERIENCED', 'TEAM'],
  ['BEST SERVICE', 'GUARANTEE'],
  ['24x7 CUSTOMER', 'SUPPORT'],
];

function TrustStrip(): JSX.Element {
  return (
    <div className="flex items-center gap-4 px-7 pb-[9px] pt-2">
      {TRUST.map(([top, bottom], i) => (
        <div key={top} className="flex flex-1 items-center gap-2">
          {i > 0 && <span className="h-[22px] w-px flex-none" style={{ background: RULE }} />}
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#E10600" strokeWidth="2">
            <circle cx="12" cy="12" r="9" />
            <path d="m8.5 12 2.4 2.4 4.6-4.8" />
          </svg>
          <span
            className="text-[7.6px] font-semibold uppercase leading-[1.3] tracking-[.3px]"
            style={{ color: MUTED }}
          >
            {top}
            <br />
            {bottom}
          </span>
        </div>
      ))}
    </div>
  );
}

/** Red-gradient footer band, on both sheets. */
function FooterBand({ page, total }: { page: number; total: number }): JSX.Element {
  return (
    <footer className="mt-auto flex items-stretch">
      <div
        className="relative flex w-[300px] flex-none items-center py-3 pl-[26px] pr-[30px]"
        style={{
          background: 'linear-gradient(100deg,#E10600,#8B0000)',
          clipPath: 'polygon(0 0, 100% 0, calc(100% - 22px) 100%, 0 100%)',
        }}
      >
        <div className="flex items-center gap-[11px]">
          <img src="/brand/guruji-mark-white.png" alt="" className="h-auto w-[34px] flex-none" />
          <div>
            <div className="text-[14.5px] font-heading leading-none tracking-[.3px] text-white">
              {COMPANY.displayName}
            </div>
            <div className="mt-[6px] text-[9.5px] leading-none text-white/85">
              {COMPANY.tagline} {COMPANY.taglineAccent}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-1 items-center gap-4 py-3 pl-2 pr-[26px]">
        <div className="flex flex-col gap-[7px] text-[10px] font-medium leading-none">
          <span>{COMPANY.phone}</span>
          <span>{COMPANY.email}</span>
          <span>{COMPANY.website}</span>
        </div>

        <div className="ml-auto text-right text-[9.7px] leading-[1.55]" style={{ color: '#2a2726' }}>
          {COMPANY.address.lines.map((line) => (
            <div key={line}>{line}</div>
          ))}
          <div>{formatCompanyLocality()}</div>
          <div className="mt-[6px] flex justify-end">
            <span className="bg-ink px-2 py-1 text-[9px] font-heading leading-[1.2] tracking-[.7px] text-white">
              GSTIN
            </span>
            <span className="bg-white px-2 py-1 text-[9.6px] font-bold leading-[1.2] tracking-[.4px]">
              {COMPANY.gstin}
            </span>
          </div>
          <div className="mt-[5px] text-[8px]" style={{ color: MUTED }}>
            Page {page} of {total}
          </div>
        </div>
      </div>
    </footer>
  );
}

/**
 * The two-page A4 voucher.
 *
 * `booking` is OPTIONAL and only consulted when there is no invoice — see
 * `voucher-content.ts` for what a token-stage voucher prints. Screens that
 * already hold the booking (the booking detail page) pass it so the sheet is
 * fully populated from the first payment; the vouchers list, which loads
 * documents rather than bookings, does not, and the unknown fields print as an
 * em dash rather than as a guess.
 */
export function VoucherDocument({
  v,
  booking,
}: {
  v: VoucherWithInvoiceDto;
  booking?: VoucherBookingContext;
}): JSX.Element {
  const { voucher } = v;
  const content = resolveVoucherContent(v, booking);
  // The export prints the address over two lines; a single stored string is
  // split on the last comma so a long one still balances across both.
  const [addr1, addr2] = splitAddress(content.guestAddress);

  return (
    <div className="flex flex-col items-center gap-6">
      {/* ─────────────────────────── PAGE 1 ─────────────────────────── */}
      <Sheet>
        <Letterhead v={v} content={content} />
        <TrustStrip />

        <div className="flex flex-col gap-[13px] px-7 pb-4">
          <div className="grid grid-cols-2 gap-3">
            <Card label="Guest Information">
              <div className="flex flex-col gap-[5px]">
                <Row label="Lead Guest Name" value={content.guestName} />
                <Row label="Contact Number" value={content.guestPhone} />
                <Row label="Alternate Number" value={voucher.alternateNumber} />
                <Row label="Email ID" value={content.guestEmail} />
                <Row label="Address" value={addr1} />
                <Row label="" value={addr2} />
                <Row
                  label="Total Guests"
                  value={`Adults: ${voucher.adults}    Kids: ${voucher.kids}`}
                />
              </div>
            </Card>

            <Card label="Tour Details">
              <div className="flex flex-col gap-[5px]">
                <Row label="Package Name" value={content.packageName} />
                <Row label="Travel Date" value={maybeDate(content.travelDate)} />
                <Row label="Return Date" value={maybeDate(content.returnDate)} />
                <Row label="Duration" value={content.duration} />
                <Row label="Departure City" value={content.departureCity} />
                <Row label="Destination" value={content.destination} />
              </div>
            </Card>
          </div>

          <Card label="Reporting & Pickup">
            <div className="grid grid-cols-2 gap-x-3 gap-y-[5px]">
              <Row label="Pickup Point" value={voucher.pickupPoint} />
              <Row label="Reporting Time" value={voucher.reportingTime} />
              <Row label="Pickup Time" value={voucher.pickupTime} />
              <Row
                label="Tour Manager"
                value={`${voucher.tourManager}${
                  voucher.tourManagerPhone ? ` · ${voucher.tourManagerPhone}` : ''
                }`}
              />
              <div className="col-span-2">
                <Row label="Vehicle Details" value={voucher.vehicleDetails} />
              </div>
            </div>
          </Card>

          <Card label="Passenger Details" className="pb-0">
            <table className="w-full table-fixed border-collapse">
              <thead>
                <tr className="bg-brand">
                  <th className={cn(TH, 'w-[34px] text-center')}>#</th>
                  <th className={TH}>Passenger Name</th>
                  <th className={cn(TH, 'w-[46px] text-center')}>Age</th>
                  <th className={cn(TH, 'w-[62px]')}>Gender</th>
                  <th className={cn(TH, 'w-[92px]')}>ID Proof Type</th>
                  <th className={cn(TH, 'w-[120px]')}>ID Proof Number</th>
                </tr>
              </thead>
              <tbody>
                {content.passengers.map((p, i) => (
                  <tr key={p.id} style={{ borderTop: `1px solid ${RULE}` }}>
                    <td className={cn(TD, 'text-center font-semibold')}>{i + 1}</td>
                    <td className={cn(TD, 'font-semibold')}>{p.name}</td>
                    <td className={cn(TD, 'text-center')}>{p.age}</td>
                    <td className={TD}>{printedGender(p)}</td>
                    {/* Legacy spellings ("aadhar"/"adhar card") are mapped to the
                        canonical label for DISPLAY only — the stored value, and
                        the ID NUMBER below, are never touched or masked. */}
                    <td className={TD}>{displayIdType(p.idType) || UNKNOWN}</td>
                    <td className={TD}>{p.idNumber || UNKNOWN}</td>
                  </tr>
                ))}
                {content.passengers.length === 0 && (
                  <tr style={{ borderTop: `1px solid ${RULE}` }}>
                    <td className={cn(TD, 'text-center')} colSpan={6} style={{ color: MUTED }}>
                      No passengers recorded.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Card>

        </div>

        <FooterBand page={1} total={2} />
      </Sheet>

      {/* ─────────────────────────── PAGE 2 ─────────────────────────── */}
      <Sheet>
        <header className="relative px-7 pt-[22px]">
          <div className="flex items-end gap-4">
            <div className="flex flex-none items-center gap-[11px]">
              <img src="/brand/guruji-mark.png" alt="" className="h-auto w-[46px] flex-none" />
              <div>
                <div className="font-heading text-[21px] leading-none tracking-[-.5px] text-brand">
                  GURUJI
                </div>
                <div className="mt-[5px] text-[11.5px] font-bold leading-none tracking-[1.1px]">
                  TRAVEL YATRA
                </div>
              </div>
            </div>
            <div className="ml-auto text-right">
              <div className="text-[17px] font-heading leading-none tracking-[-.2px]">
                BOOKING CONFIRMATION VOUCHER
              </div>
              <div
                className="mt-2 flex items-center justify-end gap-[10px] text-[10.2px] font-semibold leading-none"
                style={{ color: '#5c5653' }}
              >
                <span>
                  Voucher No. <span className="font-heading text-brand">{voucher.ref}</span>
                </span>
                <span className="h-[11px] w-px" style={{ background: RULE }} />
                <span>{content.guestName}</span>
                <span className="h-[11px] w-px" style={{ background: RULE }} />
                <span>{maybeDate(content.travelDate)}</span>
              </div>
            </div>
          </div>
          <div className="mt-[14px] h-[2px] bg-brand" />
        </header>

        <div className="flex flex-col gap-[13px] px-7 pt-3">
          {/*
            TRANSPORT DETAILS — the voucher's OWN transport legs, read from the
            raw `voucher` like Stay / Services below, NOT from `content.transport`
            (the invoice's separate transport model, which this card no longer
            reads). Absent entirely when there are no legs — no heading over an
            empty table. Times are wall-clock strings, reformatted as text by
            `formatWallClock`, never through a zone-aware formatter. An unknown
            arrival or a blank seat/PNR prints as an empty cell.

            The "Carrier / No." column header becomes mode-specific (e.g.
            "Train No.") only when every leg shares one mode — each row's own
            Mode cell already disambiguates a mixed set, so a joined header
            like "Train / Bus No." was rejected as needless and unbounded in
            length for a fixed-width print column.
          */}
          {voucher.transportLegs.length > 0 && (
            <Card label="Transport Details" className="pb-0">
              <table className="w-full table-fixed border-collapse">
                <thead>
                  <tr className="bg-brand">
                    <th className={cn(TH, 'w-[58px]')}>Mode</th>
                    <th className={cn(TH, 'w-[84px]')}>
                      {voucher.transportLegs.every((l) => l.mode === voucher.transportLegs[0]?.mode)
                        ? TRANSPORT_LEG_CARRIER_LABEL[voucher.transportLegs[0]!.mode]
                        : CARRIER_OR_NUMBER_FALLBACK_LABEL}
                    </th>
                    <th className={TH}>From</th>
                    <th className={TH}>To</th>
                    <th className={cn(TH, 'w-[92px]')}>Departure</th>
                    <th className={cn(TH, 'w-[92px]')}>Arrival</th>
                    <th className={cn(TH, 'w-[72px]')}>Seat / PNR</th>
                  </tr>
                </thead>
                <tbody>
                  {voucher.transportLegs.map((leg) => (
                    <tr key={leg.id} style={{ borderTop: `1px solid ${RULE}` }}>
                      <td className={cn(TD, 'font-semibold')}>
                        {TRANSPORT_LEG_MODE_LABEL[leg.mode]}
                      </td>
                      <td className={TD}>{leg.carrierOrNumber}</td>
                      <td className={TD}>{leg.fromPlace}</td>
                      <td className={TD}>{leg.toPlace}</td>
                      <td className={TD}>{formatWallClock(leg.departureAt)}</td>
                      <td className={TD}>
                        {leg.arrivalAt === null ? '' : formatWallClock(leg.arrivalAt)}
                      </td>
                      <td className={TD}>{leg.seatOrPnr.trim()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          {/*
            HOTEL DETAILS (item 5) — the voucher's OWN hotel-stay rows, read
            from the raw `voucher`, not from `content`. REPLACES the old
            "Stay / Services" card (the voucher's vendor snapshot): staff
            asked for one hotel section, not two overlapping ones. Absent
            entirely when there are no rows — no heading over an empty
            table. The disclaimer directly below is ONE constant
            (`VOUCHER_HOTEL_DETAILS_NOTE`) so its wording can change in one
            place later, and it only prints alongside the table it belongs to.
          */}
          {voucher.hotelDetails.length > 0 && (
            <Card label="Hotel Details" className="pb-0">
              <table className="w-full table-fixed border-collapse">
                <thead>
                  <tr className="bg-brand">
                    <th className={TH}>Hotel</th>
                    <th className={cn(TH, 'w-[76px]')}>City</th>
                    <th className={cn(TH, 'w-[86px]')}>Check-in</th>
                    <th className={cn(TH, 'w-[86px]')}>Check-out</th>
                    <th className={cn(TH, 'w-[88px]')}>Room</th>
                    <th className={cn(TH, 'w-[72px]')}>Rooms</th>
                    <th className={cn(TH, 'w-[76px]')}>Meal Plan</th>
                  </tr>
                </thead>
                <tbody>
                  {voucher.hotelDetails.map((row) => (
                    <tr key={row.id} style={{ borderTop: `1px solid ${RULE}` }}>
                      <td className={cn(TD, 'font-semibold')}>{row.hotelName}</td>
                      <td className={TD}>{row.city}</td>
                      <td className={TD}>{maybeDate(row.checkInDate)}</td>
                      <td className={TD}>{maybeDate(row.checkOutDate)}</td>
                      <td className={TD}>{row.roomType}</td>
                      <td className={TD}>{row.rooms}</td>
                      <td className={TD}>{row.mealPlan}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="px-2 py-[6px] text-[9.4px] leading-[1.3]" style={{ color: MUTED }}>
                {VOUCHER_HOTEL_DETAILS_NOTE}
              </p>
            </Card>
          )}

          {/*
            Each list prints only when it has items — a heading over an empty
            list reads as an omission on a document the traveller keeps.
          */}
          {(voucher.includes.length > 0 || voucher.excludes.length > 0) && (
            <div className="grid grid-cols-2 gap-3">
              {voucher.includes.length > 0 && (
                <Card label="Package Includes">
                  <ul className="flex flex-col gap-[5px]">
                    {voucher.includes.map((item) => (
                      <li key={item} className="flex gap-2 text-[10.1px] leading-[1.3]">
                        <span className="mt-[4px] h-[4px] w-[4px] flex-none rounded-full bg-success" />
                        <span className="min-w-0 break-words">{item}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              )}

              {voucher.excludes.length > 0 && (
                <Card label="Package Excludes">
                  <ul className="flex flex-col gap-[5px]">
                    {voucher.excludes.map((item) => (
                      <li key={item} className="flex gap-2 text-[10.1px] leading-[1.3]">
                        <span className="mt-[4px] h-[4px] w-[4px] flex-none rounded-full bg-brand" />
                        <span className="min-w-0 break-words" style={{ color: '#5c5653' }}>
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </Card>
              )}
            </div>
          )}

          <Card label="Important Notes">
            <ul className="flex flex-col gap-[5px]">
              {content.notes.map((note) => (
                <li key={note} className="flex gap-2 text-[10.1px] leading-[1.3]">
                  <span className="mt-[4px] h-[4px] w-[4px] flex-none rounded-full bg-ink" />
                  <span className="min-w-0 break-words">{note}</span>
                </li>
              ))}
              {content.notes.length === 0 && (
                <li className="text-[10.1px] leading-[1.3]" style={{ color: MUTED }}>
                  No additional notes.
                </li>
              )}
            </ul>
          </Card>

          <div className="grid grid-cols-[1.1fr_1fr] gap-3">
            {/*
              CANCELLATION POLICY (item 2) — the voucher's OWN text (a
              creation-time snapshot of the tour's policy, independently
              editable thereafter), falling back to
              `DEFAULT_CANCELLATION_POLICY_TEXT` when neither the voucher nor
              its tour ever had one set. REPLACES the old Window/Charge table
              read from `content.cancellation` (an `Invoice`-sourced
              structure with no editing UI anywhere, so it never printed real
              data — see the note on `Invoice.cancellation`/
              `InvoiceCancellationRow`, left completely untouched; only this
              page stopped reading it). `whitespace-pre-wrap` preserves line
              breaks and bullet-like lines verbatim, the same way a PDF
              viewer would render them, and wraps rather than overflowing the
              fixed-width card.
            */}
            <Card label="Cancellation Policy" className="pb-0">
              <p
                className="whitespace-pre-wrap break-words px-1 py-2 text-[10.1px] leading-[1.4]"
              >
                {voucher.cancellationPolicy ?? DEFAULT_CANCELLATION_POLICY_TEXT}
              </p>
            </Card>

            {content.money !== null ? (
              <Card label="Payment Summary">
                <div className="flex flex-col">
                  {[
                    ['Package Cost', content.money.packageCost],
                    ['Extra Charges', content.money.extraCharges],
                    ['Discount', -content.money.discount],
                    [`of which GST (${content.money.gstRate}%)`, content.money.gstAmount],
                  ].map(([label, amount]) => (
                    <div
                      key={String(label)}
                      className="flex items-center justify-between py-[6px] text-[10.1px] font-medium leading-none"
                      style={{ borderBottom: `1px solid #f2eceb` }}
                    >
                      <span className="whitespace-nowrap">{label}</span>
                      <span className="tabular-nums">{formatCurrency(amount as number)}</span>
                    </div>
                  ))}

                  <div className="my-[6px] flex items-center justify-between bg-brand px-[10px] py-[9px] text-white">
                    <span className="text-[11px] font-heading leading-none tracking-[.4px]">
                      TOTAL AMOUNT
                    </span>
                    <span className="text-[11px] font-heading leading-none tabular-nums">
                      {formatCurrency(content.money.total)}
                    </span>
                  </div>

                  <div
                    className="flex items-center justify-between py-[6px] text-[10.1px] font-medium leading-none"
                    style={{ borderBottom: `1px solid #f2eceb` }}
                  >
                    <span>Advance Paid</span>
                    <span className="tabular-nums">
                      {formatCurrency(content.money.advanceReceived)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-[6px] text-[10.1px] font-bold leading-none">
                    <span>Balance Due</span>
                    <span className="tabular-nums text-brand">
                      {formatCurrency(content.money.balance)}
                    </span>
                  </div>

                  <div
                    className="mt-2 flex items-center gap-2 border-t pt-2 text-[10px] font-semibold leading-none"
                    style={{ borderColor: '#f2eceb' }}
                  >
                    <span>Payment Status</span>
                    <span
                      className="ml-auto rounded-[3px] px-2 py-1 text-[9px] font-bold uppercase tracking-[.5px] text-white"
                      style={{ background: content.money.balance <= 0 ? '#1c8a4d' : '#E10600' }}
                    >
                      {content.money.balance <= 0 ? 'Paid in full' : 'Balance due'}
                    </span>
                  </div>

                  {voucher.balanceDueDate !== null && (
                    <div className="mt-[9px] flex items-baseline gap-1.5 text-[10.1px] font-semibold leading-none">
                      <span className="flex-none">Balance Due Date :</span>
                      <span className="text-brand">{formatShortDate(voucher.balanceDueDate)}</span>
                    </div>
                  )}
                </div>
              </Card>
            ) : (
              /*
                NO INVOICE YET — the token-stage voucher.

                Deliberately NOT the money block with zeroes in it. A printed
                "TOTAL AMOUNT ₹0" reads as a price, and this document goes to the
                traveller. What it prints instead is what it actually knows: which
                booking this is, how many guests, and when the balance is due —
                plus a plain sentence saying the tax invoice follows. See
                `voucher-content.ts` for the full reasoning.
              */
              <Card label="Booking Reference">
                <div className="flex flex-col gap-[5px]">
                  <Row label="Booking No." value={content.bookingRef} />
                  <Row
                    label="Total Guests"
                    value={`Adults: ${voucher.adults}    Kids: ${voucher.kids}`}
                  />
                  <Row
                    label="Balance Due Date"
                    value={
                      voucher.balanceDueDate === null
                        ? UNKNOWN
                        : formatShortDate(voucher.balanceDueDate)
                    }
                  />
                  <p
                    className="mt-[6px] border-t pt-2 text-[9.6px] leading-[1.4]"
                    style={{ borderColor: '#f2eceb', color: MUTED }}
                  >
                    Your seat is confirmed. The tax invoice for this booking is issued once the
                    balance is settled in full — this voucher is your travel document.
                  </p>
                </div>
              </Card>
            )}
          </div>
        </div>

        <FooterBand page={2} total={2} />
      </Sheet>
    </div>
  );
}

/**
 * One stored address -> the export's two printed lines.
 *
 * Split on the last comma so the second line carries the city/pin and the first
 * takes the rest; a short address simply leaves line two empty rather than
 * forcing an awkward break.
 */
function splitAddress(address: string): [string, string] {
  const trimmed = address.trim();
  if (trimmed.length <= 42) return [trimmed, ''];
  const cut = trimmed.lastIndexOf(',', Math.floor(trimmed.length * 0.62));
  if (cut <= 0) return [trimmed, ''];
  return [trimmed.slice(0, cut).trim(), trimmed.slice(cut + 1).trim()];
}
